import React, { useState, useCallback, useEffect } from 'react';
import { useAsync } from 'react-use';

import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import {
  TextField,
  makeStyles,
  InputAdornment,
  CircularProgress,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Autocomplete } from '@material-ui/lab';

const useStyles = makeStyles(() => ({
  autocomplete: {
    '& .MuiInputBase-root': {
      fontSize: 13,
    },
  },
}));

interface TeamOption {
  name: string;
  title?: string;
}

interface TeamSelectorProps {
  value: string | undefined;
  displayName?: string;
  onChange: (value: string | undefined) => void;
  disabled?: boolean;
}

/**
 * Team selector that properly handles initial values.
 * Shows the display name initially and fetches options on search.
 * If no displayName is provided, fetches the team's title from the catalog.
 */
export const TeamSelector = ({
  value,
  displayName,
  onChange,
  disabled,
}: TeamSelectorProps) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [search, setSearch] = useState<string>('');
  const [initialTitle, setInitialTitle] = useState<string | undefined>(
    displayName,
  );
  const [options, setOptions] = useState<TeamOption[]>(() => {
    // Initialize with current value if provided
    if (value) {
      return [{ name: value, title: displayName }];
    }
    return [];
  });

  // Fetch the team's title on mount if we have a value but no displayName
  useEffect(() => {
    if (value && !displayName) {
      catalogApi
        .getEntityByRef(`group:default/${value}`)
        .then(entity => {
          if (entity) {
            const title = entity.metadata.title || entity.metadata.name;
            setInitialTitle(title);
            setOptions(prev => {
              const updated = prev.map(opt =>
                opt.name === value ? { ...opt, title } : opt,
              );
              // If the option wasn't in the list, add it
              if (!updated.some(opt => opt.name === value)) {
                updated.push({ name: value, title });
              }
              return updated;
            });
          }
        })
        .catch(() => {
          // Silently fail - will just show the name
        });
    }
  }, [value, displayName, catalogApi]);

  const { loading } = useAsync(async () => {
    if (!search || search.length < 2) return;

    const queryEntitiesResponse = await catalogApi.queryEntities({
      fullTextFilter: {
        term: search,
        fields: ['metadata.name', 'metadata.title'],
      },
      filter: {
        kind: ['Group'],
        'spec.type': 'supervisory-org',
      },
    });

    const newOptions = queryEntitiesResponse.items.map(item => ({
      name: item.metadata.name,
      title: item.metadata.title,
    }));

    // Deduplicate and include existing value
    const optionMap = new Map<string, TeamOption>();
    if (value) {
      optionMap.set(value, { name: value, title: initialTitle || displayName });
    }
    newOptions.forEach(opt => optionMap.set(opt.name, opt));
    options.forEach(opt => optionMap.set(opt.name, opt));

    setOptions(Array.from(optionMap.values()));
  }, [search]);

  const selectedOption =
    options.find(opt => opt.name === value) ||
    (value ? { name: value, title: initialTitle || displayName } : null);

  const handleChange = useCallback(
    (_: any, selected: TeamOption | null) => {
      onChange(selected?.name);
    },
    [onChange],
  );

  const handleInputChange = useCallback(
    (_: any, newInputValue: string, reason: string) => {
      if (reason === 'input' && newInputValue.length >= 2) {
        setSearch(newInputValue);
      } else if (reason === 'clear') {
        setSearch('');
      }
    },
    [],
  );

  return (
    <Autocomplete
      className={classes.autocomplete}
      options={options}
      value={selectedOption}
      disabled={disabled}
      loading={loading}
      getOptionLabel={option => option.title || option.name}
      getOptionSelected={(option, selected) => option.name === selected.name}
      onChange={handleChange}
      onInputChange={handleInputChange}
      popupIcon={<ExpandMoreIcon />}
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          size="small"
          placeholder="Search for a team..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={option => option.title || option.name}
    />
  );
};
